import React, { type FocusEvent } from 'react';
import type { AIResponse } from '../common/types.tsx';
import AIContainer from '../common/ui/ai-container/index.tsx';
import { PromptContainer, usePromptIsLoading } from '../controllers/prompt-state/index.tsx';
import type { PromptId } from '../controllers/prompt-state/types.tsx';
import { PromptResponse, type Props as PromptResponseProps } from './prompt-response/index.tsx';
import { SearchField } from './search-field/index.tsx';

type Props<Payload> = PromptResponseProps<Payload> & {
	/**
	 * `true` to autofocus the AI input.
	 */
	autoFocus?: boolean;

	/**
	 * Optional aria-label to render for the Back button.
	 */
	backButtonLabel?: string;

	/**
	 * Event emitted when the back button is pressed in AI inline prompt component.
	 */
	onBack?: () => void;

	/**
	 * Event emitted when the user focuses on the AI inline prompt input.
	 */
	onFocus?: (event: FocusEvent<HTMLInputElement>) => void;

	/**
	 * Placeholder or series of placeholders to show in the AI input. If an array is provided the component will cycle
	 * through each placeholder with a typing animation.
	 */
	placeholder: string | string[];
};

function AIInlinePrompt<Payload>({
	autoFocus,
	backButtonLabel,
	children,
	onBack,
	onFocus,
	placeholder,
	...props
}: Props<Payload>) {
	const [isLoading] = usePromptIsLoading();

	return (
		<AIContainer
			shouldFitContainer
			spacing="none"
			isLoading={isLoading}
			testId="atlassian-intelligence.ui.ai-inline-prompt"
		>
			<SearchField
				autoFocus={autoFocus}
				backButtonLabel={backButtonLabel}
				onBack={onBack}
				onFocus={onFocus}
				placeholder={placeholder}
			/>
			<PromptResponse {...props}>{children}</PromptResponse>
		</AIContainer>
	);
}

export type AIInlinePromptProps<Payload> = Props<Payload> & {
	/**
	 * Event emitted when a prompt is submitted. This returns a promise containing a response payload to be used when
	 * rendering nested content within this component.
	 *
	 * @param input AI prompt input text
	 * @param unique ID for each prompt submitted
	 */
	onPrompt: (input: string, promptId: PromptId) => Promise<AIResponse<Payload>>;

	/**
	 * Event emitted when a successful response is returned for a submitted prompt.
	 *
	 * @param response Successful reponse payload
	 */
	onPromptSuccess?: (response: Payload) => void;
};

function AIInlinePromptWithContainer<Payload>({
	children,
	onPrompt,
	onPromptSuccess,
	...props
}: AIInlinePromptProps<Payload>) {
	return (
		<PromptContainer onPrompt={onPrompt} onPromptSuccess={onPromptSuccess}>
			<AIInlinePrompt {...props}>{children}</AIInlinePrompt>
		</PromptContainer>
	);
}

export default AIInlinePromptWithContainer;

// eslint-disable-next-line @atlassian/eng-health/no-barrel-files/disallow-reexports
export { Feedback } from './feedback';
